import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import { rgba } from 'polished';
import { createPortal } from 'react-dom';
import { FocusOn } from 'react-focus-on';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { neutralColors } from '../../styles/colors/colors.js';
import { spacing4x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { Backdrop } from '../Backdrop/Backdrop.js';
import { Box } from '../Box/Box.js';
import { Button } from '../Button/Button.js';
import { IconClose } from '../Icons/IconClose.js';
import { LinkButton } from '../LinkButton/LinkButton.js';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';

var _excluded = ["closeOnOutsideClick", "onOutsideClick", "onEscKeyDown", "open", "autoFocus", "className", "children", "footer", "maxModalHeight", "size", "header", "onClose", "displayCloseButton", "footerButtonProps", "containerSelector"];
var modalWidthMap = {
  xxs: 320,
  xs: 400,
  sm: 600,
  md: 800,
  lg: 1040,
  xl: 1400
};
var ModalContent = styled(Box).withConfig({
  displayName: "Modal__ModalContent",
  componentId: "sc-1dinmru-0"
})(["&&{background:", ";outline:none;position:relative;max-width:calc(100vw - 64px);display:flex;flex-direction:column;}"], neutralColors.white);
var CloseIconButton = styled(LinkButton).withConfig({
  displayName: "Modal__CloseIconButton",
  componentId: "sc-1dinmru-1"
})(["&&{position:absolute;top:20px;right:20px;}"]);
var ContentWrapper = styled.div.withConfig({
  displayName: "Modal__ContentWrapper",
  componentId: "sc-1dinmru-2"
})(["&&{overflow:auto;}"]);
var FooterWrapper = styled.div.withConfig({
  displayName: "Modal__FooterWrapper",
  componentId: "sc-1dinmru-3"
})(["&&{padding:", ";border-top:1px solid ", ";}"], spacing4x, neutralColors.silver);
var FooterButtonsContainer = styled.div.withConfig({
  displayName: "Modal__FooterButtonsContainer",
  componentId: "sc-1dinmru-4"
})(["&&{display:flex;justify-content:flex-end;}"]);

var Modal = function Modal(props) {
  var _props$closeOnOutside = props.closeOnOutsideClick,
      closeOnOutsideClick = _props$closeOnOutside === void 0 ? false : _props$closeOnOutside,
      onOutsideClick = props.onOutsideClick,
      onEscKeyDown = props.onEscKeyDown,
      _props$open = props.open,
      open = _props$open === void 0 ? true : _props$open,
      autoFocus = props.autoFocus,
      className = props.className,
      children = props.children,
      footer = props.footer,
      maxModalHeight = props.maxModalHeight,
      _props$size = props.size,
      size = _props$size === void 0 ? "sm" : _props$size,
      header = props.header,
      onClose = props.onClose,
      _props$displayCloseBu = props.displayCloseButton,
      displayCloseButton = _props$displayCloseBu === void 0 ? true : _props$displayCloseBu,
      footerButtonProps = props.footerButtonProps,
      containerSelector = props.containerSelector,
      rest = _objectWithoutProperties(props, _excluded);

  var _useId = useId(1, "modal-"),
      _useId2 = _slicedToArray(_useId, 1),
      titleId = _useId2[0];

  var width = modalWidthMap[size];

  var _onEscapeKey = onEscKeyDown || onClose;

  var _onClickOutside = onOutsideClick || (closeOnOutsideClick ? onClose : void 0);

  var bgColor = rgba(neutralColors.smoke, 0.75);
  var paddingRight = displayCloseButton ? 48 : spacing4x;
  var paddingStyles = {
    padding: spacing4x,
    paddingRight: paddingRight
  };
  var footerSection;
  var headerSection;

  if (header) {
    headerSection = typeof header === "function" ? header(titleId) : /*#__PURE__*/jsx("h4", {
      "aria-level": 1,
      id: titleId,
      children: header
    });
  }

  if (footer) {
    footerSection = footer;
  } else if (footerButtonProps !== null && footerButtonProps !== void 0 && footerButtonProps.length) {
    footerSection = /*#__PURE__*/jsx(FooterButtonsContainer, {
      children: footerButtonProps.map(function (btnProps, i) {
        return /*#__PURE__*/jsx(Button, _objectSpread({
          style: {
            marginLeft: i === 0 ? 0 : spacing4x
          }
        }, btnProps), i);
      })
    });
  }

  var _useThemeContext = useThemeContext(),
      defaultPortalContainerSelector = _useThemeContext.defaultPortalContainerSelector;

  function getPortalEl() {
    var selector = containerSelector || defaultPortalContainerSelector;
    if (!selector) return document.body;
    return document.querySelector(selector) || document.body;
  }

  return open ? /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/createPortal( /*#__PURE__*/jsx(Backdrop, {
      fullscreen: true,
      style: {
        overflow: "auto",
        background: bgColor
      },
      open: true,
      children: /*#__PURE__*/jsx(FocusOn, {
        autoFocus: autoFocus,
        onClickOutside: _onClickOutside,
        onEscapeKey: _onEscapeKey,
        children: /*#__PURE__*/jsxs(ModalContent, _objectSpread(_objectSpread({
          className: className,
          "aria-modal": "true",
          role: "dialog",
          "aria-labelledby": titleId,
          tabIndex: 0,
          elevation: 3,
          borderRadius: 2,
          style: {
            width: width,
            maxHeight: maxModalHeight || "calc(100vh - 64px)"
          }
        }, rest), {}, {
          children: [headerSection && /*#__PURE__*/jsx("div", {
            style: _objectSpread(_objectSpread({}, paddingStyles), {}, {
              borderBottom: children || footerSection ? "1px solid ".concat(neutralColors.silver) : void 0
            }),
            children: headerSection
          }), children && /*#__PURE__*/jsx(ContentWrapper, {
            style: paddingStyles,
            id: headerSection ? void 0 : titleId,
            children: children
          }), footerSection && /*#__PURE__*/jsx(FooterWrapper, {
            children: footerSection
          }), displayCloseButton && /*#__PURE__*/jsx(CloseIconButton, {
            label: "Close",
            variant: "secondary",
            hideLabel: true,
            iconSize: 16,
            onClick: onClose,
            startAdornment: IconClose
          })]
        }))
      })
    }), getPortalEl())
  }) : null;
};

Modal.propTypes = {
  "autoFocus": _PropTypes.bool,
  "children": _PropTypes.node,
  "className": _PropTypes.string,
  "closeOnOutsideClick": _PropTypes.bool,
  "containerSelector": _PropTypes.string,
  "displayCloseButton": _PropTypes.bool,
  "footer": _PropTypes.node,
  "footerButtonProps": _PropTypes.arrayOf(_PropTypes.object),
  "header": _PropTypes.oneOfType([_PropTypes.func, _PropTypes.string]),
  "maxModalHeight": _PropTypes.number,
  "onClose": _PropTypes.func.isRequired,
  "onEscKeyDown": _PropTypes.func,
  "onOutsideClick": _PropTypes.func,
  "open": _PropTypes.bool,
  "size": _PropTypes.oneOf(["lg", "md", "sm", "xl", "xs", "xxs"])
};
Modal.displayName = "Modal";

export { Modal };
